import { useReactFlow } from '@xyflow/react'
import { useCallback, useEffect, useRef } from 'react'

import { DayNightNodeData } from '../../../converters/shared/reactFlowTypes'
import { getToggleCodeState } from '../../../utils/dayNightSettings'
import { useDayNightSettings } from '../../Canvas/SwitchboardCanvas'

/**
 * Hook for Day/Night node functionality.
 * Handles toggle code selection and status.
 */
export const useDayNightNode = (id: string, data: DayNightNodeData) => {
  const { updateNodeData } = useReactFlow()
  const { settings } = useDayNightSettings()
  const dataRef = useRef(data)

  // Keep dataRef in sync with data
  useEffect(() => {
    dataRef.current = data
  }, [data])

  // toggleCode can be null (Advanced mode, uses *3) or a string like "*30"-"*39" (Basic mode)
  const currentToggleCode = data.toggleCode ?? null

  // Prefer PBX-derived status when settings are available so legacy/admin changes
  // are reflected even if local draft data has stale status.
  const statusFromSettings = settings
    ? getToggleCodeState(settings, currentToggleCode)
    : undefined
  const currentStatus = statusFromSettings || data.status || 'open'

  const handleToggleCodeChange = useCallback(
    (toggleCode: string | null) => {
      const statusForToggleCode = settings
        ? getToggleCodeState(settings, toggleCode)
        : dataRef.current.status

      // Update toggle code
      updateNodeData(id, {
        ...dataRef.current,
        toggleCode,
        status: statusForToggleCode,
      })
    },
    [id, updateNodeData, settings],
  )

  const handleStatusChange = useCallback(
    (status: 'open' | 'closed') => {
      updateNodeData(id, {
        ...dataRef.current,
        status,
      })
    },
    [id, updateNodeData],
  )

  // Sync status from PBX settings when available.
  // This keeps Day/Night parity with legacy/admin state and prevents stale draft status.
  useEffect(() => {
    if (!settings) return

    const resolvedStatus = getToggleCodeState(settings, currentToggleCode)
    if (data.status !== resolvedStatus) {
      updateNodeData(id, {
        ...dataRef.current,
        status: resolvedStatus,
      })
    }
  }, [data.status, settings, currentToggleCode, id, updateNodeData])

  return {
    currentToggleCode,
    currentStatus,
    handleToggleCodeChange,
    handleStatusChange,
    hasData: true, // Always has data (status is always set)
  }
}
